import {
  buildDocumentPath,
  formatAsOfDateSegment,
} from '@/lib/utils/documentMetadata';
import legislationServices from '@/services/legislation-services';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLegislationDataProps {
  segments: string[];
}

type Translator = ReturnType<typeof useTranslations<'LegislationDetails'>>;

/** Copy for a resolution that came back empty — dated when asOf was sent. */
function unresolvedMessage(t: Translator, asOf: string | null): string {
  const formattedAsOf = asOf ? formatAsOfDateSegment(asOf) : null;
  return formattedAsOf
    ? t('noVersionAsOf', { date: formattedAsOf })
    : t('notFound');
}

export function useGetLegislationInformation({
  segments,
}: UseLegislationDataProps) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const locale = useLocale();
  const router = useRouter();
  // Deliberately NOT in the fetch callback's deps below: `useTranslations()`
  // is not guaranteed to return a referentially stable function (some
  // test-harness overrides hand back a new closure per call), and a `t` dep
  // would re-run the fetch effect on every render — an infinite resolve loop
  // (see useClaimVerification.test.ts for the same hazard). The messages it
  // reads are fixed for the page's lifetime, so a stale closure is harmless.
  const t = useTranslations('LegislationDetails');
  // Issue #327 gap 1: a cross-reference link forwards the citing law's
  // effective date as ?asOf=YYYYMMDD (see link-options-menu.tsx) so this
  // resolution picks THAT version instead of always "latest".
  const asOf = useSearchParams().get('asOf');
  // Capture hash before any async work so it's not lost in navigation
  const hashRef = useRef<string>(
    typeof window !== 'undefined' ? window.location.hash : ''
  );

  const fetchLegislationDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (segments.length === 5) {
      setIsLoading(false);
      return;
    }

    try {
      const iri = segments.join('/');
      const legislationIri =
        await legislationServices.getLegislationRedirectIri(iri, asOf);
      if (!legislationIri.data) {
        // The backend deliberately returns null rather than falling back to
        // "latest" when no version was in force on `asOf` (#327 gap 1) — a
        // wrong current text is worse than none. That makes this branch
        // reachable for perfectly valid laws (a 1975-dated citing page
        // linking to a 2015 act), so it has to SAY so instead of leaving the
        // loading skeleton up forever. isLoading is cleared only here and in
        // catch: on success we navigate away, and clearing it first would
        // flash the unresolved page underneath the redirect.
        setError(unresolvedMessage(t, asOf));
        setIsLoading(false);
        return;
      }
      // Preserve the hash part from the current URL for section navigation.
      // Use window.location.href instead of router.push because Next.js App
      // Router strips hash fragments from router.push() calls.
      const hash = hashRef.current;
      const redirectPath = `${buildDocumentPath(locale, legislationIri.data)}${hash}`;
      if (hash) {
        window.location.href = redirectPath;
      } else {
        router.push(redirectPath);
      }
    } catch (error) {
      setError(error as string);
      setIsLoading(false);
    }
  }, [segments, locale, router, asOf]);

  useEffect(() => {
    fetchLegislationDetails();
  }, [fetchLegislationDetails]);

  return {
    isLoading,
    error,
  };
}
